// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import { createApi, fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import config from '../config';

// Token storage utility (localStorage fallback for browsers blocking third-party cookies)
export const tokenStorage = {
  getAccessToken: () => localStorage.getItem('vms_access_token'),
  getRefreshToken: () => localStorage.getItem('vms_refresh_token'),
  setTokens: (accessToken: string, refreshToken: string) => {
    localStorage.setItem('vms_access_token', accessToken);
    localStorage.setItem('vms_refresh_token', refreshToken);
    // Broadcast token update to other tabs
    localStorage.setItem('vms_token_updated', Date.now().toString());
  },
  clearTokens: () => {
    localStorage.removeItem('vms_access_token');
    localStorage.removeItem('vms_refresh_token');
    // Broadcast logout to other tabs
    localStorage.setItem('vms_logged_out', Date.now().toString());
  },
};

// Listen for token changes from other tabs (handles multi-tab sync)
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'vms_logged_out') {
      // Another tab logged out - reload to sync state
      console.log('[AUTH] Logout detected from another tab, reloading...');
      window.location.reload();
    }
    if (event.key === 'vms_token_updated') {
      // Another tab refreshed tokens - they're now in localStorage
      console.log('[AUTH] Token update detected from another tab');
    }
  });
}

// Parse JWT to get expiration time
const parseJwtExpiration = (token: string): number | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch {
    return null;
  }
};

// Check if token is about to expire (within 5 minutes)
const isTokenExpiringSoon = (token: string): boolean => {
  const expirationTime = parseJwtExpiration(token);
  if (!expirationTime) return true; // Treat invalid tokens as expiring
  const fiveMinutes = 5 * 60 * 1000;
  return Date.now() >= expirationTime - fiveMinutes;
};

// Mutex to prevent multiple simultaneous refresh attempts (handles multi-tab race condition)
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

const baseQuery = fetchBaseQuery({
  baseUrl: config.API_BASE_URL,
  credentials: "include", // Keep for browsers that support cookies
  prepareHeaders: (headers) => {
    // Add Authorization header if we have a token in localStorage
    // This is a fallback for browsers blocking third-party cookies
    const token = tokenStorage.getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// Proactive token refresh function
const doRefresh = async (api: any, extraOptions: any): Promise<boolean> => {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) return false;
  
  console.log('[AUTH] Proactively refreshing token...');
  
  const refreshResult = await baseQuery(
    {
      url: '/users/refresh-token',
      method: 'POST',
      body: { refreshToken },
    },
    api,
    extraOptions
  );
  
  if (refreshResult.data) {
    const { accessToken, refreshToken: newRefreshToken } = refreshResult.data as {
      accessToken: string;
      refreshToken: string;
    };
    
    console.log('[AUTH] Token refresh successful');
    tokenStorage.setTokens(accessToken, newRefreshToken);
    return true;
  } else {
    console.log('[AUTH] Token refresh failed, clearing tokens');
    tokenStorage.clearTokens();
    return false;
  }
};

// Wrapper that handles automatic token refresh
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Skip refresh check for the refresh endpoint itself
  const requestUrl = typeof args === 'string' ? args : args.url;
  const isRefreshRequest = requestUrl?.includes('/users/refresh-token');
  
  // Proactive refresh: Check if token is about to expire before making the request
  if (!isRefreshRequest) {
    const currentToken = tokenStorage.getAccessToken();
    
    if (currentToken && isTokenExpiringSoon(currentToken)) {
      // Prevent multiple simultaneous refresh attempts
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = doRefresh(api, extraOptions);
      }
      
      if (refreshPromise) {
        await refreshPromise;
        isRefreshing = false;
        refreshPromise = null;
      }
    }
  }
  
  let result = await baseQuery(args, api, extraOptions);

  const isAuthEndpoint =
    requestUrl?.includes('/users/login') ||
    requestUrl?.includes('/users/refresh-token') ||
    requestUrl?.includes('/users/me');
  
  // Fallback: If we still get a 401, try to refresh (handles edge cases)
  if (result.error && result.error.status === 401 && !isRefreshRequest) {
    const refreshToken = tokenStorage.getRefreshToken();
    
    if (refreshToken && !isRefreshing) {
      console.log('[AUTH] Access token expired unexpectedly, attempting refresh...');
      isRefreshing = true;
      
      const success = await doRefresh(api, extraOptions);
      isRefreshing = false;
      
      if (success) {
        // Retry the original request with new token
        result = await baseQuery(args, api, extraOptions);
      }
    } else {
      tokenStorage.clearTokens();
    }
  }

  // Any explicit unauthorized on auth endpoints should clear stale local tokens.
  if (result.error && result.error.status === 401 && isAuthEndpoint) {
    tokenStorage.clearTokens();
  }
  
  return result;
};

// initialize an empty api service that we'll inject endpoints into later as needed
export const emptySplitApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
})
